import { CalendarPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  calendarChoices,
  calendarPlatform,
  type CalendarChoice,
  type CalendarLinks,
  type CalendarPlatform,
} from "../lib/calendarLinks";

/**
 * "Add to calendar" on the booked screen and on each upcoming visit in the client area
 * (boona docs/codebase/google-calendar/README.md §9.7): the backend's links in the order
 * this device should see them (lib/calendarLinks.ts), quiet like the screen's other
 * secondary actions. Each opens outside the widget, which may run in an iframe
 * (embed.tsx). Nothing renders without links.
 */
export function AddToCalendar({ links, platform, align = "center", className = "" }: {
  links?: CalendarLinks | null;
  /** the device's own by default */
  platform?: CalendarPlatform;
  align?: "center" | "start";
  className?: string;
}) {
  const { t } = useTranslation();
  const choices = calendarChoices(links, platform ?? calendarPlatform(deviceUserAgent()));
  if (!choices.length) return null;
  const name: Record<CalendarChoice["id"], string> = {
    google: t("widget.calendar.google", "Google Calendar"),
    outlook: t("widget.calendar.outlook", "Outlook"),
    apple: t("widget.calendar.apple", "Apple Calendar"),
    other: t("widget.calendar.other", "Other calendar (.ics)"),
  };
  const centered = align === "center";
  return (
    <div className={`text-[13px] text-muted-foreground ${centered ? "text-center" : ""} ${className}`}>
      <div className="inline-flex items-center gap-1.5">
        <CalendarPlus className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t("widget.calendar.title", "Add to calendar")}
      </div>
      <div className={`mt-0.5 flex flex-wrap items-center gap-x-4 ${centered ? "justify-center" : ""}`}>
        {choices.map((c) => (
          <a
            key={c.id}
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
            className="py-1 font-medium text-foreground underline underline-offset-2 hover:opacity-80"
          >
            {name[c.id]}
          </a>
        ))}
      </div>
    </div>
  );
}

function deviceUserAgent(): string {
  return typeof navigator === "undefined" ? "" : navigator.userAgent ?? "";
}
